/**
 * FE-4577 — persistence for the design-system mirror.
 *
 * WHY THIS IS NOT `utils/indexedDbHelper.ts`. That module is hardcoded to the
 * `main` database and its single `cacheScreens` store, and its `getItem`
 * returns a `[id, value]` tuple; generalising it would mean touching the hot
 * navigation path at every existing call site. More importantly the ticket's
 * suggestion — a second store inside `main`, behind a version bump — carries a
 * real hazard: a tab still running the previous bundle holds `main` at v1, the
 * v2 upgrade fires `onblocked`, and the open never settles. Every screen-cache
 * read is `yield call(...)`ed from a saga, so that is a navigation that hangs.
 * A separate database has no upgrade to block, keeps the (large) design record
 * out of `getItemRegexp`'s `getAll()` on every navigation, and isolates
 * design-system failures from the screen cache entirely.
 *
 * Nothing here throws or rejects: a failure is reported as "storage unusable",
 * which the store turns into a memory-only session.
 */

const DB_NAME = 'fractalDesignSystem';
const DB_VERSION = 1;
const STORE_NAME = 'designSystem';
const OPEN_TIMEOUT_MS = 3000;

/** `undefined` = storage unusable · `null` = usable, nothing stored under that key. */
export type ReadResult = Record<string, any> | null | undefined;

let dbPromise: Promise<IDBDatabase> | null = null;

const warn = (operation: string, error: unknown) => {
  const err = error as { message?: string; name?: string } | null;
  console.warn(`[design-system] IndexedDB ${operation} failed:`, err?.message || error);
};

function openDatabase(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined' || indexedDB === null) {
      reject(new Error('IndexedDB is unavailable in this context'));
      return;
    }

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      reject(error);
      return;
    }

    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    const finish = (fn: (arg?: any) => void, arg?: any) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(arg);
    };
    timer = setTimeout(() => finish(reject, new Error('IndexedDB open timed out')), OPEN_TIMEOUT_MS);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onblocked = () => finish(reject, new Error('IndexedDB upgrade blocked by another tab'));

    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      db.onclose = () => {
        dbPromise = null;
      };
      finish(resolve, db);
    };

    request.onerror = () => finish(reject, request.error);
  }).catch((error) => {
    warn('open', error);
    dbPromise = null;
    throw error;
  });

  return dbPromise;
}

export async function readRecord(key: string): Promise<ReadResult> {
  try {
    const db = await openDatabase();
    return await new Promise<ReadResult>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      transaction.onabort = () => reject(transaction.error);
      const request = transaction.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve(request.result ? request.result.value : null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    warn('read', error);
    dbPromise = null;
    return undefined;
  }
}

/** Resolves `true` when the write landed. Never rejects. */
export async function writeRecord(key: string, value: Record<string, any>): Promise<boolean> {
  try {
    const db = await openDatabase();
    return await new Promise<boolean>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      transaction.onabort = () => reject(transaction.error);
      const request = transaction.objectStore(STORE_NAME).put({ id: key, value });
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    warn('write', error);
    dbPromise = null;
    return false;
  }
}
